/** Pure derivations for the Customers directory + detail drawer (staff console). No I/O; unit-tested. */
import type { Customer360 } from '../api';

/** List row of GET /v1/customers. status, ownerId, services and whatsapp come from the API; ownerLabel and health have no API source yet (filters stay disabled until rows carry them). */
export interface CustomerRow {
  id: string; name: string; email: string | null; planCode?: string | null;
  status?: string | null; automation?: string | null; health?: string | null; ownerId?: string | null; ownerLabel?: string | null;
  services?: readonly string[] | null; whatsapp?: string | null;
}

export const HEALTH_BANDS = ['healthy', 'watch', 'at_risk'] as const;
export type HealthBand = (typeof HEALTH_BANDS)[number];
/** Normalises a health value ("at-risk", "At risk", "good") to a band; unknown values have no band. */
export function healthBand(h: string | null | undefined): HealthBand | null {
  const s = (h ?? '').toLowerCase().replace(/[\s-]+/g, '_');
  if (s === 'healthy' || s === 'good') return 'healthy';
  if (s === 'watch' || s === 'warning') return 'watch';
  if (s === 'at_risk' || s === 'risk' || s === 'critical') return 'at_risk';
  return null;
}

export type CustomerStage = 'active' | 'onboarding' | 'paused';
/** Stage of a row from its status / automation state. Rows without either are active. */
export function customerStage(r: Pick<CustomerRow, 'status' | 'automation'>): CustomerStage {
  const s = (r.status ?? '').toLowerCase();
  if (s === 'onboarding' || s === 'pending' || s === 'invited' || s === 'provisioning') return 'onboarding';
  if (s === 'paused' || s === 'suspended' || r.automation === 'suspended') return 'paused';
  return 'active';
}

export type FilterKey = 'health' | 'owner' | 'service' | 'whatsapp' | 'status';
/** Which list-level fields the loaded rows actually carry. A filter whose field is absent everywhere cannot work and is disabled. */
export function filterAvailability(rows: readonly CustomerRow[]): Record<FilterKey, boolean> {
  return {
    health: rows.some((r) => healthBand(r.health) !== null),
    owner: rows.some((r) => !!r.ownerId),
    service: rows.some((r) => (r.services?.length ?? 0) > 0),
    whatsapp: rows.some((r) => r.whatsapp !== undefined),
    status: rows.some((r) => r.status != null || r.automation != null),
  };
}

/** Distinct plan codes in the loaded rows, sorted, for the Plan filter. */
export const planOptions = (rows: readonly CustomerRow[]): string[] =>
  [...new Set(rows.map((r) => r.planCode).filter((p): p is string => !!p))].sort((a, b) => a.localeCompare(b));
export const healthOptions = (rows: readonly CustomerRow[]): HealthBand[] => HEALTH_BANDS.filter((b) => rows.some((r) => healthBand(r.health) === b));
export const ownerOptions = (rows: readonly CustomerRow[]): { id: string; label: string }[] => {
  const m = new Map<string, string>();
  for (const r of rows) if (r.ownerId && !m.has(r.ownerId)) m.set(r.ownerId, r.ownerLabel || r.ownerId);
  return [...m].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
};
export const serviceOptions = (rows: readonly CustomerRow[]): string[] =>
  [...new Set(rows.flatMap((r) => r.services ?? []))].sort((a, b) => a.localeCompare(b));
export const WHATSAPP_STATES: readonly ChannelState[] = ['connected', 'action', 'none'];
export const whatsappOptions = (rows: readonly CustomerRow[]): ChannelState[] =>
  WHATSAPP_STATES.filter((s) => rows.some((r) => r.whatsapp !== undefined && whatsappState(r.whatsapp) === s));

export interface CustomerFilters { plan?: string; health?: string; owner?: string; service?: string; whatsapp?: string; tab?: CustomerStage }
/** Client-side filter over the loaded page ('' = any). The text query is applied by the server. */
export const filterCustomers = <T extends CustomerRow>(rows: readonly T[], f: CustomerFilters): T[] =>
  rows.filter((r) =>
    (!f.plan || r.planCode === f.plan)
    && (!f.health || healthBand(r.health) === f.health)
    && (!f.owner || r.ownerId === f.owner)
    && (!f.service || (r.services ?? []).includes(f.service))
    && (!f.whatsapp || (r.whatsapp !== undefined && whatsappState(r.whatsapp) === f.whatsapp))
    && (!f.tab || customerStage(r) === f.tab));

/** Deliverables done/total from the Customer 360 panel; an item is done at 100% progress. */
export function deliverableCount(c: Pick<Customer360['panels'], 'deliverables'>): { done: number; total: number } {
  const items = c.deliverables.items;
  return { done: items.filter((i) => i.progressPct >= 100).length, total: items.length };
}

export type ChannelState = 'connected' | 'action' | 'none';
/** Maps the WhatsApp channel status to the three states the directory shows. */
export function whatsappState(status: string | null | undefined): ChannelState {
  const s = (status ?? '').toLowerCase();
  if (s === 'connected' || s === 'active' || s === 'ready') return 'connected';
  if (!s || s === 'none' || s === 'not_connected' || s === 'not_started' || s === 'disconnected_by_user') return 'none';
  return 'action';
}
export const CHANNEL_TONE: Record<ChannelState, 'good' | 'warn' | 'neutral'> = { connected: 'good', action: 'warn', none: 'neutral' };

/** Newest monthly report (periods are YYYY-MM so a string sort is chronological). */
export const latestReport = (reports: Customer360['panels']['reports']): Customer360['panels']['reports'][number] | null =>
  [...reports].sort((a, b) => (a.period < b.period ? 1 : a.period > b.period ? -1 : 0))[0] ?? null;

export const DRAWER_TABS = ['summary', 'people', 'services', 'access', 'whatsapp'] as const;
export type DrawerTab = (typeof DRAWER_TABS)[number];
export const drawerTab = (v: string | null): DrawerTab => (DRAWER_TABS as readonly string[]).includes(v ?? '') ? (v as DrawerTab) : 'summary';
export const CUSTOMER_TABS = ['active', 'onboarding', 'paused'] as const;
export type CustomerTab = (typeof CUSTOMER_TABS)[number];
export const customerTab = (v: string | null): CustomerTab => (CUSTOMER_TABS as readonly string[]).includes(v ?? '') ? (v as CustomerTab) : 'active';
